export type SearchFieldKind = 'text' | 'identifier';

export interface SearchFieldInput {
  value: unknown;
  /** Higher-priority fields win when two results match in the same way. */
  priority?: number;
  /** Identifiers default to false; ordinary text defaults to true. */
  typoTolerant?: boolean;
  kind?: SearchFieldKind;
}

interface PreparedSearchField {
  normalized: string;
  tokens: string[];
  priority: number;
  typoTolerant: boolean;
}

export interface PreparedSearchDocument {
  fields: PreparedSearchField[];
}

export type SearchMatchKind = 'exact' | 'prefix' | 'substring' | 'typo';

export interface SearchMatch {
  kind: SearchMatchKind;
  score: number;
}

export interface PreparedSearchEntry<T> {
  item: T;
  document: PreparedSearchDocument;
}

const CHARACTER_REPLACEMENTS: Readonly<Record<string, string>> = {
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ł: 'l',
  ð: 'd',
  þ: 'th',
};

const MATCH_SCORES: Readonly<Record<SearchMatchKind, number>> = {
  exact: 400,
  prefix: 300,
  substring: 200,
  typo: 100,
};

function collectPrimitiveValues(value: unknown, output: string[], seen: WeakSet<object>): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    output.push(String(value));
    return;
  }
  if (value instanceof Date) {
    output.push(value.toISOString());
    return;
  }
  if (typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);
  if (Array.isArray(value)) {
    for (const item of value) collectPrimitiveValues(item, output, seen);
    return;
  }
  for (const nested of Object.values(value as Record<string, unknown>)) {
    collectPrimitiveValues(nested, output, seen);
  }
}

function stringifySearchValue(value: unknown): string {
  const values: string[] = [];
  collectPrimitiveValues(value, values, new WeakSet());
  return values.join(' ');
}

/** Locale-independent normalization shared by every browser-side search. */
export function normalizeSearchText(value: string): string {
  const decomposed = value.normalize('NFKD').toLowerCase();
  let transliterated = '';
  for (const character of decomposed) {
    if (/\p{M}/u.test(character)) continue;
    transliterated += CHARACTER_REPLACEMENTS[character] ?? character;
  }
  return transliterated
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenizeSearchQuery(query: string): string[] {
  const normalized = normalizeSearchText(query);
  return normalized ? normalized.split(' ') : [];
}

export function prepareSearchDocument(fields: readonly SearchFieldInput[]): PreparedSearchDocument {
  return {
    fields: fields.flatMap(field => {
      const normalized = normalizeSearchText(stringifySearchValue(field.value));
      if (!normalized) return [];
      const kind = field.kind ?? 'text';
      return [
        {
          normalized,
          tokens: normalized.split(' '),
          priority: field.priority ?? 0,
          typoTolerant: field.typoTolerant ?? kind === 'text',
        },
      ];
    }),
  };
}

function isAlphabeticToken(token: string): boolean {
  return token.length >= 4 && /^\p{L}+$/u.test(token);
}

/** Exact one insertion, deletion, substitution, or adjacent transposition. */
export function isWithinOneSearchTypo(left: string, right: string): boolean {
  if (left === right) return true;
  if (!isAlphabeticToken(left) || !isAlphabeticToken(right)) return false;
  if (Math.abs(left.length - right.length) > 1) return false;

  if (left.length === right.length) {
    const mismatches: number[] = [];
    for (let index = 0; index < left.length; index += 1) {
      if (left[index] !== right[index]) {
        mismatches.push(index);
        if (mismatches.length > 2) return false;
      }
    }
    if (mismatches.length === 1) return true;
    if (mismatches.length !== 2) return false;
    const [first, second] = mismatches;
    return second === first + 1 && left[first] === right[second] && left[second] === right[first];
  }

  const shorter = left.length < right.length ? left : right;
  const longer = left.length < right.length ? right : left;
  let shorterIndex = 0;
  let longerIndex = 0;
  let skipped = false;
  while (shorterIndex < shorter.length && longerIndex < longer.length) {
    if (shorter[shorterIndex] === longer[longerIndex]) {
      shorterIndex += 1;
      longerIndex += 1;
      continue;
    }
    if (skipped) return false;
    skipped = true;
    longerIndex += 1;
  }
  return true;
}

function tokenMatchKind(
  queryToken: string,
  candidateToken: string,
  typoTolerant: boolean
): SearchMatchKind | null {
  if (candidateToken === queryToken) return 'exact';
  if (candidateToken.startsWith(queryToken)) return 'prefix';
  if (candidateToken.includes(queryToken)) return 'substring';
  if (typoTolerant && isWithinOneSearchTypo(queryToken, candidateToken)) return 'typo';
  return null;
}

function lowerQuality(left: SearchMatchKind | null, right: SearchMatchKind): SearchMatchKind {
  if (left === null) return right;
  return MATCH_SCORES[right] < MATCH_SCORES[left] ? right : left;
}

export function matchPreparedSearch(
  document: PreparedSearchDocument,
  query: string
): SearchMatch | null {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return { kind: 'exact', score: 0 };
  const queryTokens = normalizedQuery.split(' ');
  let score = 0;
  let worstKind: SearchMatchKind | null = null;

  const exactPhraseField = document.fields.find(field => field.normalized === normalizedQuery);
  if (exactPhraseField) score += 10_000 + exactPhraseField.priority * 10;

  for (const queryToken of queryTokens) {
    let best: { kind: SearchMatchKind; score: number } | undefined;
    for (const field of document.fields) {
      for (const candidateToken of field.tokens) {
        const kind = tokenMatchKind(queryToken, candidateToken, field.typoTolerant);
        if (!kind) continue;
        const candidateScore = MATCH_SCORES[kind] + field.priority * 10;
        if (!best || candidateScore > best.score) best = { kind, score: candidateScore };
      }
    }
    if (!best) return null;
    score += best.score;
    worstKind = lowerQuality(worstKind, best.kind);
  }

  return { kind: worstKind ?? 'exact', score };
}

export function matchesPreparedSearch(document: PreparedSearchDocument, query: string): boolean {
  return matchPreparedSearch(document, query) !== null;
}

export function matchesSearch(fields: readonly SearchFieldInput[], query: string): boolean {
  return matchesPreparedSearch(prepareSearchDocument(fields), query);
}

export function rankPreparedSearchEntries<T>(
  entries: readonly PreparedSearchEntry<T>[],
  query: string
): T[] {
  if (!query.trim()) return entries.map(entry => entry.item);
  return entries
    .flatMap((entry, index) => {
      const match = matchPreparedSearch(entry.document, query);
      return match ? [{ entry, index, match }] : [];
    })
    .sort((left, right) => right.match.score - left.match.score || left.index - right.index)
    .map(row => row.entry.item);
}

export function rankSearchItems<T>(
  items: readonly T[],
  query: string,
  fieldsForItem: (item: T) => readonly SearchFieldInput[]
): T[] {
  return rankPreparedSearchEntries(
    items.map(item => ({ item, document: prepareSearchDocument(fieldsForItem(item)) })),
    query
  );
}
